import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchActivePlans } from "../../services/clientPlansService";
import { PLAN_TYPE_META } from "../../../common/constants";
import styles from "./clientPlansPageStyles";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import useTokenFromUrl from "../../hooks/useTokenFromUrl";

const ClientPlansPage = () => {
 const { user } = useAuth();
 const { loading: tokenLoading } = useTokenFromUrl();
 const [plans, setPlans] = useState([]);
 const [hoveredCard, setHoveredCard] = useState(null);
 const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 const [showLoginAlert, setShowLoginAlert] = useState(false);

 const navigate = useNavigate();

 useEffect(() => {
 loadPlans();

 const handleResize = () => setIsMobile(window.innerWidth <= 768);
 window.addEventListener("resize", handleResize);
 return () => window.removeEventListener("resize", handleResize);
 }, []);

 const loadPlans = async () => {
 try {
 const response = await fetchActivePlans();
 // Filter out free plans before setting state
 const filteredPlans = (response.data || []).filter((plan) => !plan.isFree);
 setPlans(filteredPlans);
 } catch (error) {
 }
 };

 const handleSubscribe = (plan) => {
 if (!user) {
 setShowLoginAlert(true);
 return;
 }
 navigate(`/checkout/${plan.id}`);
 };

 const getPlanAccentColor = (planType) => {
 switch (planType) {
 case "ONE_ON_ONE":       return "#007BFF";
 case "GROUP":            return "#6f42c1";
 case "PARTNER_PRACTICE": return "#e91e8c";
 case "COMBO":            return "#fd7e14";
 default:                 return "#007BFF";
 }
 };

 const getPlanTypeLabel = (planType) =>
 PLAN_TYPE_META[planType]?.displayName ?? planType;

 const applyResponsiveStyle = (baseStyle) =>
 isMobile && baseStyle?.mobile ? { ...baseStyle, ...baseStyle.mobile } : baseStyle;

 if (tokenLoading) {
 return (
 <>
 <Navbar />
 <div style={{ textAlign: "center", marginTop: "100px" }}>
 <p>Loading...</p>
 </div>
 </>
 );
 }

 return (
 <>
 <Navbar />
 <div style={applyResponsiveStyle(styles.pageContainer)}>
 <h2 style={applyResponsiveStyle(styles.title)}>Choose Your Perfect Plan</h2>
 <p style={applyResponsiveStyle(styles.subtitle)}>
 Practice smarter with plans designed for every learning style
 </p>

 <div style={applyResponsiveStyle(styles.planGrid)}>
 {plans.map((plan) => {
 const accentColor = getPlanAccentColor(plan.planType);
 const isCombo = plan.planType === "COMBO";
 const totalComboSessions =
 (plan.sessionsGroup || 0) +
 (plan.sessionsOneOnOne || 0) +
 (plan.sessionsPartnerPractice || 0);

 return (
 <div
 key={plan.id}
 style={{
 ...applyResponsiveStyle(styles.planCard),
 borderTop: `6px solid ${accentColor}`,
 ...(hoveredCard === plan.id ? styles.planCardHover : {}),
 }}
 onMouseEnter={() => setHoveredCard(plan.id)}
 onMouseLeave={() => setHoveredCard(null)}
 >
 <span
 style={{
 ...applyResponsiveStyle(styles.planTypeChip),
 color: accentColor,
 background: `${accentColor}15`,
 }}
 >
 {getPlanTypeLabel(plan.planType)}
 </span>

 <h3 style={{ ...applyResponsiveStyle(styles.planName), color: accentColor }}>
 {plan.name}
 </h3>

 <p style={applyResponsiveStyle(styles.planDescription)}>
 {plan.description || PLAN_TYPE_META[plan.planType]?.description}
 </p>

 <div style={{ ...applyResponsiveStyle(styles.priceSection), color: accentColor }}>
 {plan.price === 0 ? "Free" : `₹${plan.price}`}
 </div>
 <span style={applyResponsiveStyle(styles.perPlanText)}>per plan</span>

 <ul style={applyResponsiveStyle(styles.featuresList)}>
 {isCombo ? (
 <>
 <li style={applyResponsiveStyle(styles.featureItem)}>
 <strong>{totalComboSessions}</strong> total sessions
 </li>
 <li style={applyResponsiveStyle(styles.featureItem)}>
 <strong>{plan.sessionsGroup || 0}</strong> Group sessions
 </li>
 <li style={applyResponsiveStyle(styles.featureItem)}>

 </li>
 <li style={applyResponsiveStyle(styles.featureItem)}>
                    <strong>{plan.sessionsOneOnOne || 0}</strong> One-on-One sessions
                  </li>
                  <li style={applyResponsiveStyle(styles.featureItem)}>
                    <strong>{plan.sessionsPartnerPractice || 0}</strong> Partner Practice sessions
                  </li>
 </>
 ) : (
 <>
 <li style={applyResponsiveStyle(styles.featureItem)}>
 <strong>{plan.totalSessions}</strong> sessions
 </li>
 <li style={applyResponsiveStyle(styles.featureItem)}>
 <strong>{plan.sessionDurationMinutes} mins</strong> per session
 </li>
 </>
 )}
 </ul>

 <button
 style={{ ...applyResponsiveStyle(styles.button), background: accentColor }}
 onClick={() => handleSubscribe(plan)}
 >
 Subscribe Now
 </button>
 </div>
 );
 })}
 </div>
 </div>

 {/* LOGIN ALERT MODAL */}
 {showLoginAlert && (
 <div
 style={{
 position: "fixed",
 inset: 0,
 background: "rgba(0,0,0,0.5)",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 zIndex: 10000,
 padding: "16px",
 }}
 >
 <div
 style={{
 background: "#fff",
 borderRadius: "16px",
 maxWidth: "400px",
 width: "100%",
 padding: "32px 24px",
 boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
 textAlign: "center",
 }}
 >
 <h2
 style={{
 fontSize: "1.6rem",
 fontWeight: "700",
 marginBottom: "12px",
 color: "#222",
 }}
 >
 Login Required
 </h2>
 <p style={{ fontSize: "1rem", color: "#555", marginBottom: "24px" }}>
 You need to login or register to subscribe to a plan.
 </p>

 <div
 style={{
 display: "flex",
 gap: "12px",
 justifyContent: "center",
 flexWrap: "wrap",
 }}
 >
 <button
 style={{
 padding: "10px 20px",
 borderRadius: "8px",
 border: "1px solid #ccc",
 background: "#fff",
 color: "#333",
 cursor: "pointer",
 minWidth: "120px",
 fontWeight: 500,
 transition: "all 0.2s",
 }}
 onClick={() => setShowLoginAlert(false)}
 >
 Dismiss
 </button>

 <button
 style={{
 padding: "10px 20px",
 borderRadius: "8px",
 border: "none",
 background: "#00A8CC",
 color: "#fff",
 cursor: "pointer",
 minWidth: "120px",
 fontWeight: 600,
 transition: "all 0.2s",
 }}
 onClick={() => navigate("/login")}
 >
 Go to Login
 </button>
 </div>
 </div>
 </div>
 )}
 </>
 );
};

export default ClientPlansPage;
